import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Download,
  Trash2,
  RefreshCw,
  CloudDownload,
  Database,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  Settings,
  Info,
  AlertTriangle
} from 'lucide-react';
import { backupApi } from '../../services/backupApi';
import { BackupItem, BackupStats } from '../../types';
import dayjs from 'dayjs';

const Backup: React.FC = () => {
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });

  // Popconfirm state
  const [confirmState, setConfirmState] = useState<{
    type: 'delete' | 'cleanup' | 'fixStuck' | null;
    backupId?: string;
  }>({ type: null });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadBackups();
    loadStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const response = await backupApi.list({
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });

      setBackups(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0
      }));
    } catch (error) {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await backupApi.getStats();
      setStats(response.data || null);
    } catch (error) {
      console.error('Failed to load backup stats:', error);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      await backupApi.create();
      toast.success('Backup creation started');
      loadBackups();
      loadStats();
    } catch (error) {
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDownload = async (backupId: string) => {
    try {
      const blob = await backupApi.download(backupId);

      // Create blob and download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${backupId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleDelete = async (backupId: string) => {
    try {
      await backupApi.delete(backupId);
      toast.success('Backup deleted successfully');
      loadBackups();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const handleCleanup = async () => {
    try {
      const response = await backupApi.cleanup();
      toast.success(`Cleaned up ${response.data?.deletedCount || 0} old backups`);
      loadBackups();
      loadStats();
    } catch (error) {
      toast.error('Failed to cleanup old backups');
    }
  };

  const handleFixStuck = async () => {
    try {
      const response = await backupApi.fixStuck();
      toast.success(`Fixed ${response.data?.fixedCount || 0} stuck backups`);
      loadBackups();
      loadStats();
    } catch (error) {
      toast.error('Failed to fix stuck backups');
    }
  };

  const getStatusBadge = (status: string, errorMessage?: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
      pending: {
        bg: 'bg-blue-100 text-blue-700',
        text: 'text-blue-700',
        icon: <Clock className="w-3 h-3" />,
        label: 'PENDING'
      },
      in_progress: {
        bg: 'bg-orange-100 text-orange-700',
        text: 'text-orange-700',
        icon: <Clock className="w-3 h-3" />,
        label: 'IN PROGRESS'
      },
      completed: {
        bg: 'bg-green-100 text-green-700',
        text: 'text-green-700',
        icon: <CheckCircle className="w-3 h-3" />,
        label: 'COMPLETED'
      },
      failed: {
        bg: 'bg-red-100 text-red-700',
        text: 'text-red-700',
        icon: <AlertCircle className="w-3 h-3" />,
        label: 'FAILED'
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];

    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium w-fit ${config.bg}`}>
          {config.icon}
          {config.label}
        </span>
        {errorMessage && (
          <span title={errorMessage}>
            <AlertTriangle className="w-4 h-4 text-red-500" />
          </span>
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  const handleConfirmAction = async () => {
    if (confirmState.type === 'delete' && confirmState.backupId) {
      await handleDelete(confirmState.backupId);
    } else if (confirmState.type === 'cleanup') {
      await handleCleanup();
    } else if (confirmState.type === 'fixStuck') {
      await handleFixStuck();
    }
    setConfirmState({ type: null });
  };

  const confirmMessages: Record<string, { title: string; description: string }> = {
    delete: {
      title: 'Delete Backup',
      description: 'Are you sure you want to delete this backup? This action cannot be undone.'
    },
    cleanup: {
      title: 'Cleanup Old Backups',
      description: 'This will delete backups older than the retention period. Continue?'
    },
    fixStuck: {
      title: 'Fix Stuck Backups',
      description: 'This will mark backups stuck in progress as failed. Continue?'
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Backup Management</h1>

      {/* Confirmation Modal */}
      {confirmState.type && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {confirmMessages[confirmState.type]?.title}
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              {confirmMessages[confirmState.type]?.description}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                onClick={() => setConfirmState({ type: null })}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                onClick={handleConfirmAction}
              >
                {confirmState.type === 'delete' ? 'Delete' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards — Row 1 */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Database className="w-4 h-4" />
              Total Backups
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.totalBackups}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Successful
            </div>
            <div className="text-2xl font-bold text-green-700">{stats.successfulBackups}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <AlertCircle className="w-4 h-4" />
              Failed
            </div>
            <div className="text-2xl font-bold text-red-600">{stats.failedBackups}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Info className="w-4 h-4" />
              Success Rate
            </div>
            <div className={`text-2xl font-bold ${stats.successRate >= 80 ? 'text-green-700' : 'text-yellow-600'}`}>
              {stats.successRate}%
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards — Row 2 */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <CloudDownload className="w-4 h-4" />
              Total Size
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.totalSize}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Database className="w-4 h-4" />
              Average Size
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.averageSize}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Clock className="w-4 h-4" />
              Last Backup
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.lastBackup ? dayjs(stats.lastBackup).format('MMM DD, YYYY') : 'Never'}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60"
            onClick={handleCreateBackup}
            disabled={creatingBackup}
          >
            {creatingBackup ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create Backup
          </button>

          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={loadBackups}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
            onClick={() => setConfirmState({ type: 'cleanup' })}
          >
            <Settings className="w-4 h-4" />
            Cleanup
          </button>

          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
            onClick={() => setConfirmState({ type: 'fixStuck' })}
          >
            <AlertCircle className="w-4 h-4" />
            Fix Stuck
          </button>

          <select
            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">Filter by status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
          </select>

          <div className="flex items-center gap-2">
            <input
              type="date"
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start Date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="End Date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Health Status Alert */}
      {stats && stats.successRate < 80 && (
        <div className="flex items-start gap-3 px-4 py-3 mb-4 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-800">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-yellow-600" />
          <div>
            <div className="font-semibold text-sm">Backup Health Warning</div>
            <div className="text-sm">
              Backup success rate is {stats.successRate}%. Consider checking backup logs for issues.
            </div>
          </div>
        </div>
      )}

      {/* Backups Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : backups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Database className="w-10 h-10 mb-2 text-gray-300" />
            <span className="text-sm">No backups found</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Backup ID</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Created</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Records</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Size</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {backups.map((record) => (
                    <tr key={record.backupId} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-3">
                        <code className="text-xs bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-mono">
                          {record.backupId}
                        </code>
                      </td>
                      <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                        {dayjs(record.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                      </td>
                      <td className="py-3 px-3">
                        {getStatusBadge(record.status, record.errorMessage)}
                      </td>
                      <td className="py-3 px-3 text-gray-700">
                        {record.totalRecords.toLocaleString()}
                      </td>
                      <td className="py-3 px-3 text-gray-700">{record.totalSize}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <button
                            title="Download Backup"
                            className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={record.status !== 'completed'}
                            onClick={() => handleDownload(record.backupId)}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            title="Delete Backup"
                            className="p-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                            onClick={() => setConfirmState({ type: 'delete', backupId: record.backupId })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {pagination.total > 0 && (
                  <>
                    {((pagination.current - 1) * pagination.pageSize) + 1}–{Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total} backups
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <select
                  className="px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pagination.pageSize}
                  onChange={(e) => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), current: 1 }))}
                >
                  {[10, 20, 50].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    disabled={pagination.current <= 1}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  >
                    &lsaquo;
                  </button>
                  <span className="text-sm text-gray-700 px-2">
                    Page {pagination.current} of {totalPages || 1}
                  </span>
                  <button
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    disabled={pagination.current >= totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  >
                    &rsaquo;
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Backup;
